import { createContext, useContext } from 'react';
import { createStore, useStore } from 'zustand';
import { store as globalStore } from './store';
import {
  DEFAULT_CONFIG,
  INITIAL_STATE,
  persistDrivingModeActive,
  persistAudioOutputDeviceId,
  persistCueSoundsEnabled,
} from './defaults';
import type {
  WidgetState,
  WidgetConfig,
  ChatMessage,
  AuthState,
  FileUploadState,
  FileAttachment,
  McpToolDef,
  ToolCallState,
  PendingConfirmation,
  DrivingModeState,
  ChatFileDownload,
} from '../types';
import type { ChatOrchestrator } from '../services/orchestrator';

// ─── Actions ─────────────────────────────────────────────────────────────────

export interface WidgetActions {
  /** Updates zustand only — used by the global-store mirror to avoid feedback loops. */
  patchState(partial: Partial<WidgetState>): void;
  setState(partial: Partial<WidgetState>): void;
  setConfig(config: Partial<WidgetConfig>): void;
  toggleOpen(): void;
  toggleExpanded(): void;
  addMessage(msg: ChatMessage): void;
  removeMessage(id: string): void;
  updateMessage(id: string, partial: Partial<ChatMessage>): void;
  setAuth(auth: Partial<AuthState>): void;
  setFileUpload(fileUpload: Partial<FileUploadState>): void;
  addPendingAttachment(attachment: FileAttachment): void;
  removePendingAttachment(fileId: string): void;
  clearPendingAttachments(): FileAttachment[];
  setError(error: string | null): void;
  setMcpSession(sessionId: string | null): void;
  setTools(tools: McpToolDef[]): void;
  addToolCall(tc: ToolCallState): void;
  updateToolCall(id: string, partial: Partial<ToolCallState>): void;
  clearToolCalls(): void;
  setPendingConfirmation(pc: PendingConfirmation | null): void;
  setMessageFeedback(messageId: string, rating: 'positive' | 'negative'): void;
  addFileToMessage(messageId: string, file: ChatFileDownload): void;
  setDrivingMode(partial: Partial<DrivingModeState>): void;
  // Focus side-channel (replaces the imperative _pendingFocus on WidgetStore class)
  inputFocusPending: boolean;
  requestInputFocus(): void;
  consumeInputFocus(): void;
}

export type WidgetStoreState = WidgetState & WidgetActions;

// ─── Factory ─────────────────────────────────────────────────────────────────

export function createWidgetStore() {
  return createStore<WidgetStoreState>((set, get) => ({
    ...INITIAL_STATE,
    config: { ...DEFAULT_CONFIG },
    inputFocusPending: false,

    patchState: (partial) => {
      set(partial);
    },

    setState: (partial) => {
      set(partial);
      globalStore.setState(partial);
    },

    setConfig: (config) => {
      set((s) => ({ config: { ...s.config, ...config } }));
      globalStore.setConfig(config);
    },

    toggleOpen: () => {
      const nextIsOpen = !get().isOpen;
      set({ isOpen: nextIsOpen });
      globalStore.setState({ isOpen: nextIsOpen });
    },

    toggleExpanded: () => {
      const nextIsExpanded = !get().isExpanded;
      set({ isExpanded: nextIsExpanded });
      globalStore.setState({ isExpanded: nextIsExpanded });
    },

    addMessage: (msg) => {
      set((s) => ({ messages: [...s.messages, msg] }));
      globalStore.addMessage(msg);
    },

    removeMessage: (id) => {
      set((s) => ({ messages: s.messages.filter((m) => m.id !== id) }));
      globalStore.removeMessage(id);
    },

    updateMessage: (id, partial) => {
      set((s) => ({
        messages: s.messages.map((m) => (m.id === id ? { ...m, ...partial } : m)),
      }));
      globalStore.updateMessage(id, partial);
    },

    setAuth: (auth) => {
      set((s) => ({ auth: { ...s.auth, ...auth } }));
      globalStore.setAuth(auth);
    },

    setFileUpload: (fileUpload) => {
      set((s) => ({ fileUpload: { ...s.fileUpload, ...fileUpload } }));
      globalStore.setFileUpload(fileUpload);
    },

    addPendingAttachment: (attachment) => {
      set((s) => ({ pendingAttachments: [...s.pendingAttachments, attachment] }));
      globalStore.addPendingAttachment(attachment);
    },

    removePendingAttachment: (fileId) => {
      set((s) => ({
        pendingAttachments: s.pendingAttachments.filter((a) => a.fileId !== fileId),
      }));
      globalStore.removePendingAttachment(fileId);
    },

    clearPendingAttachments: () => {
      const attachments = [...get().pendingAttachments];
      set({ pendingAttachments: [] });
      globalStore.clearPendingAttachments();
      return attachments;
    },

    setError: (error) => {
      set({ error });
      globalStore.setError(error);
    },

    setMcpSession: (sessionId) => {
      set({ mcpSessionId: sessionId });
      globalStore.setMcpSession(sessionId);
    },

    setTools: (tools) => {
      set({ tools });
      globalStore.setTools(tools);
    },

    addToolCall: (tc) => {
      set((s) => ({ toolCalls: [...s.toolCalls, tc] }));
      globalStore.addToolCall(tc);
    },

    updateToolCall: (id, partial) => {
      set((s) => ({
        toolCalls: s.toolCalls.map((tc) => (tc.id === id ? { ...tc, ...partial } : tc)),
      }));
      globalStore.updateToolCall(id, partial);
    },

    clearToolCalls: () => {
      set({ toolCalls: [] });
      globalStore.clearToolCalls();
    },

    setPendingConfirmation: (pc) => {
      set({ pendingConfirmation: pc });
      globalStore.setPendingConfirmation(pc);
    },

    setMessageFeedback: (messageId, rating) => {
      set((s) => ({
        messages: s.messages.map((m) =>
          m.id === messageId ? { ...m, feedback: rating } : m
        ),
      }));
      globalStore.setMessageFeedback(messageId, rating);
    },

    addFileToMessage: (messageId, file) => {
      set((s) => ({
        messages: s.messages.map((m) => {
          if (m.id !== messageId) return m;
          return { ...m, files: [...(m.files || []), file] };
        }),
      }));
      globalStore.addFileToMessage(messageId, file);
    },

    setDrivingMode: (partial) => {
      const currentDrivingMode = get().drivingMode;
      const nextDrivingMode = { ...currentDrivingMode, ...partial };
      const changed = Object.keys(partial).some((key) => (
        currentDrivingMode[key as keyof DrivingModeState]
          !== nextDrivingMode[key as keyof DrivingModeState]
      ));

      if (!changed) return;

      persistDrivingModeActive(nextDrivingMode.active);
      persistAudioOutputDeviceId(nextDrivingMode.audioOutputDeviceId);
      persistCueSoundsEnabled(nextDrivingMode.drivingCueSoundsEnabled);
      set({ drivingMode: nextDrivingMode });
      globalStore.setDrivingMode(partial);
    },

    requestInputFocus: () => set({ inputFocusPending: true }),

    consumeInputFocus: () => set({ inputFocusPending: false }),
  }));
}

export type WidgetStore = ReturnType<typeof createWidgetStore>;

// ─── Contexts ─────────────────────────────────────────────────────────────────

export const WidgetStoreContext = createContext<WidgetStore | null>(null);

/** Shadow root context - gives components access to the shadow root for custom events */
export const WidgetShadowContext = createContext<ShadowRoot | null>(null);

/** Orchestrator context - a stable ref so components can call orchestrator methods */
export const WidgetOrchestratorContext = createContext<React.RefObject<ChatOrchestrator | null> | null>(null);

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useWidgetStore<T>(selector: (state: WidgetStoreState) => T): T {
  const store = useContext(WidgetStoreContext);
  if (!store) throw new Error('useWidgetStore must be used within WidgetStoreContext.Provider');
  return useStore(store, selector);
}

export function useShadow(): ShadowRoot {
  const shadow = useContext(WidgetShadowContext);
  if (!shadow) throw new Error('useShadow must be used within WidgetShadowContext.Provider');
  return shadow;
}

export function useOrchestrator(): React.RefObject<ChatOrchestrator | null> {
  const ref = useContext(WidgetOrchestratorContext);
  if (!ref) throw new Error('useOrchestrator must be used within WidgetOrchestratorContext.Provider');
  return ref;
}
